/**
 * Pure turn-indicator model: maps mate-list turn state + theme colors into
 * header paint props. No React; the thin hook reads signals/context and calls this.
 */
import type { PlayerPieceComponent } from '@merels/sdk'

export interface TurnIndicatorSeatColor {
  primary: string
  background: string
  foreground: string
}

export interface TurnIndicatorAppColor {
  main: string
  background: string
}

export interface TurnIndicatorModel {
  text: string
  /** Exact player primary — accents (bar, borders, glyph). */
  accentColor: string
  /** Player backdrop — header + turn chip fill. */
  backdropColor: string
  /** Text on the player backdrop. */
  textColor: string
  /** Ink for icons sitting on a solid primary fill (hover). */
  accentInkColor: string
  /** Active seat index for optional PlayerPiece; -1 when unknown. */
  playerIndex: number
  /** Active logical player id (e.g. "white"). */
  playerId?: string
  /** Optional game-exported piece component. */
  PlayerPiece: PlayerPieceComponent | null
  dataAttrs: { 'data-player-number': string; 'data-player-symbol': string } | null
}

export interface BuildTurnIndicatorModelInput {
  hasActiveGame: boolean
  turnMessageText: string | null
  turnMessageDataAttrs: { 'data-player-number': string; 'data-player-symbol': string } | null
  activePlayerId: string | undefined
  /** Logical player ids in seat order (index matches theme playerColors). */
  playerIds: readonly string[]
  getPlayerColor: (playerIndex: number) => TurnIndicatorSeatColor | null
  neutral: TurnIndicatorSeatColor | null | undefined
  app: TurnIndicatorAppColor
  playerPiece: PlayerPieceComponent | null
}

/** Relative-luminance ink for paint-on-primary (not a remix of the turn color). */
export function inkOnPrimary(hex: string): string {
  const raw = hex.replace('#', '')
  if (raw.length < 6) return '#fafafa'
  const r = parseInt(raw.slice(0, 2), 16) / 255
  const g = parseInt(raw.slice(2, 4), 16) / 255
  const b = parseInt(raw.slice(4, 6), 16) / 255
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b
  return luminance > 0.55 ? '#1a1a1a' : '#fafafa'
}

/** Drop trailing "(White)" / "(X)" when the piece glyph already identifies the seat. */
export function stripTrailingPlayerLabel(text: string): string {
  return text.replace(/\s*\([^)]+\)\s*$/, '').trim()
}

/** Returns null when there is no active-turn message to show. */
export function buildTurnIndicatorModel(
  input: BuildTurnIndicatorModelInput,
): TurnIndicatorModel | null {
  const {
    hasActiveGame,
    turnMessageText,
    turnMessageDataAttrs,
    activePlayerId,
    playerIds,
    getPlayerColor,
    neutral,
    app,
    playerPiece,
  } = input

  if (!hasActiveGame || !turnMessageText) return null

  const activePlayerIndex = activePlayerId
    ? playerIds.findIndex((id) => id === activePlayerId)
    : -1
  const playerColor = activePlayerIndex >= 0 ? getPlayerColor(activePlayerIndex) : null

  const accentColor = playerColor?.primary ?? neutral?.primary ?? app.main
  const backdropColor = playerColor?.background ?? neutral?.background ?? app.background
  const textColor = playerColor?.foreground ?? neutral?.foreground ?? app.main

  const showPiece = Boolean(playerPiece) && activePlayerIndex >= 0

  return {
    text: showPiece ? stripTrailingPlayerLabel(turnMessageText) : turnMessageText,
    accentColor,
    backdropColor,
    textColor,
    accentInkColor: inkOnPrimary(accentColor),
    playerIndex: activePlayerIndex,
    playerId: activePlayerId,
    PlayerPiece: playerPiece,
    dataAttrs: turnMessageDataAttrs,
  }
}
